/**
 * API Service
 * Handles communication with Firebase Functions backend
 */

import { environment } from '../core/config/firebase.js';
import { GlobalEvents, Events } from '../core/events/index.js';

class ApiService {
  constructor() {
    this.baseUrl = environment.apiUrl;
    this.initialized = false;
  }

  /**
   * Initialize the API service
   */
  async init() {
    if (this.initialized) return;

    try {
      // Test API connectivity
      await this.testConnection();
      this.initialized = true;
      console.log('✓ API Service initialized and connected');
      GlobalEvents.emit(Events.SERVICE_READY, { service: 'ApiService' });
    } catch (error) {
      console.error('✗ API Service initialization failed:', error);
      GlobalEvents.emit(Events.SERVICE_ERROR, {
        service: 'ApiService',
        error: error.message,
      });
      throw error;
    }
  }

  /**
   * Test API connection
   */
  async testConnection() {
    try {
      console.log(`Testing API connection to: ${this.baseUrl}`);

      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`API health check failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('API health check:', data);
      return data;
    } catch (error) {
      console.error('API connection test failed:', error);
      throw error;
    }
  }

  /**
   * Make authenticated API request
   */
  async request(endpoint, options = {}) {
    if (!this.initialized) {
      throw new Error('API Service not initialized');
    }

    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    try {
      console.log(`API Request: ${config.method || 'GET'} ${url}`);

      const response = await fetch(url, config);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `API request failed: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      const data = await response.json();
      console.log(`API Response:`, data);
      return data;
    } catch (error) {
      console.error(`API request error for ${endpoint}:`, error);
      GlobalEvents.emit(Events.SERVICE_ERROR, {
        service: 'ApiService',
        endpoint,
        error: error.message,
      });
      throw error;
    }
  }

  /**
   * Send password reset email via API
   */
  async sendPasswordResetEmail(email) {
    return this.request('/auth/password/reset', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  /**
   * Register new user via API
   */
  async registerUser(userData) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  /**
   * Send phone verification
   */
  async sendPhoneVerification(phoneNumber) {
    return this.request('/auth/verify-phone', {
      method: 'POST',
      body: JSON.stringify({ phoneNumber }),
    });
  }

  /**
   * Verify phone code
   */
  async verifyPhoneCode(phoneNumber, code) {
    return this.request('/auth/verify-phone-code', {
      method: 'POST',
      body: JSON.stringify({ phoneNumber, code }),
    });
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
